import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Shield, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SecurityQuestionsSection = () => {
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showQuestionsDialog, setShowQuestionsDialog] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allQuestions, setAllQuestions] = useState([]);
  const [userQuestions, setUserQuestions] = useState([
    { question: '', answer: '' },
    { question: '', answer: '' },
    { question: '', answer: '' },
    { question: '', answer: '' },
    { question: '', answer: '' }
  ]);

  useEffect(() => {
    fetchSecurityQuestions();
  }, []);

  const fetchSecurityQuestions = async () => {
    try {
      const response = await axios.get(`${API}/auth/security-questions`);
      setAllQuestions(response.data.questions);
    } catch (error) {
      console.error('Failed to fetch security questions');
    }
  };

  const handleVerifyPassword = async () => {
    if (!password) {
      toast.error('Please enter your password');
      return;
    }

    setVerifying(true);
    try {
      await axios.post(`${API}/user/verify-password?password=${encodeURIComponent(password)}`);
      
      // Load current security questions
      const response = await axios.get(`${API}/user/security-questions`);
      const currentQuestions = response.data.questions;
      
      // Populate with current questions
      setUserQuestions(currentQuestions.map(q => ({ question: q.question, answer: '' })));
      
      setShowPasswordDialog(false);
      setShowQuestionsDialog(true);
      setPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Incorrect password');
    } finally {
      setVerifying(false);
    }
  };

  const handleQuestionChange = (index, field, value) => {
    const updated = [...userQuestions];
    updated[index][field] = value;
    setUserQuestions(updated);
  };

  const handleUpdateQuestions = async () => {
    // Validate all fields
    const questions = userQuestions.map(q => q.question);
    const uniqueQuestions = new Set(questions);
    
    if (questions.some(q => !q)) {
      toast.error('Please select all 5 security questions');
      return;
    }

    if (uniqueQuestions.size !== 5) {
      toast.error('Please select 5 different security questions');
      return;
    }

    if (userQuestions.some(q => !q.answer.trim())) {
      toast.error('Please answer all security questions');
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API}/user/security-questions`, userQuestions);
      toast.success('Security questions updated successfully!');
      setShowQuestionsDialog(false);
      setUserQuestions([
        { question: '', answer: '' },
        { question: '', answer: '' },
        { question: '', answer: '' },
        { question: '', answer: '' },
        { question: '', answer: '' }
      ]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update security questions');
    } finally {
      setLoading(false);
    }
  };

  const getAvailableQuestions = (currentIndex) => {
    const selected = userQuestions
      .map((q, idx) => idx !== currentIndex ? q.question : null)
      .filter(Boolean);
    return allQuestions.filter(q => !selected.includes(q));
  };

  return (
    <>
      <Card className="shadow-lg">
        <CardHeader className="bg-gradient-to-r from-[#8B5CF6]/5 to-[#EC4899]/5">
          <CardTitle className="text-2xl text-[#8B5CF6] flex items-center gap-2">
            <Shield className="w-6 h-6" />
            Security Questions
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            <p className="text-gray-600">
              Security questions are used for password recovery. Keep your answers secret and memorable.
            </p>
            <Button
              onClick={() => setShowPasswordDialog(true)}
              className="bg-[#8B5CF6] hover:bg-[#8B5CF6]/90"
            >
              Update Security Questions
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Password Verification Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Verify Your Identity</DialogTitle>
            <DialogDescription>
              Enter your password to update security questions
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="verify-password">Password</Label>
              <div className="relative mt-1">
                <Input
                  id="verify-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleVerifyPassword()}
                  className="pr-10"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowPasswordDialog(false);
                  setPassword('');
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleVerifyPassword}
                disabled={verifying || !password}
                className="flex-1 bg-[#8B5CF6] hover:bg-[#8B5CF6]/90"
              >
                {verifying ? 'Verifying...' : 'Verify'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Security Questions Update Dialog */}
      <Dialog open={showQuestionsDialog} onOpenChange={setShowQuestionsDialog}>
        <DialogContent className="sm:max-w-[520px] max-h-[85vh] flex flex-col overflow-hidden">
          <DialogHeader className="pb-4 shrink-0">
            <DialogTitle className="text-xl">Update Security Questions</DialogTitle>
            <DialogDescription className="text-sm">
              Answer 5 security questions for password recovery
            </DialogDescription>
          </DialogHeader>

          {/* Scrollable content area */}
          <div className="overflow-y-auto flex-1 px-1">
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="text-amber-600 mt-0.5 shrink-0" size={16} />
                  <p className="text-sm text-amber-800">
                    <strong>Important:</strong> Keep these answers secret and memorable.
                  </p>
                </div>
              </div>

              {userQuestions.map((item, index) => (
                <div key={index} className="space-y-2 p-3 border rounded-lg bg-gray-50">
                  <Label className="text-sm font-medium">Question {index + 1} *</Label>
                  <Select
                    value={item.question}
                    onValueChange={(value) => handleQuestionChange(index, 'question', value)}
                  >
                    <SelectTrigger className="bg-white w-full text-sm h-10">
                      <SelectValue placeholder="Select a question" />
                    </SelectTrigger>
                    <SelectContent className="max-w-[480px]">
                      {getAvailableQuestions(index).map((question) => (
                        <SelectItem key={question} value={question} className="text-sm whitespace-normal py-2 leading-relaxed">
                          {question}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {item.question && (
                    <Input
                      type="text"
                      value={item.answer}
                      onChange={(e) => handleQuestionChange(index, 'answer', e.target.value)}
                      className="bg-white w-full text-sm h-10"
                      placeholder="Your answer"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Fixed buttons at bottom */}
          <div className="flex gap-3 pt-4 shrink-0 border-t">
            <Button
              onClick={() => {
                setShowQuestionsDialog(false);
                setUserQuestions([
                  { question: '', answer: '' },
                  { question: '', answer: '' },
                  { question: '', answer: '' },
                  { question: '', answer: '' },
                  { question: '', answer: '' }
                ]);
              }}
              variant="outline"
              className="flex-1"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateQuestions}
              disabled={loading}
              className="flex-1 bg-[#8B5CF6] hover:bg-[#8B5CF6]/90"
            >
              {loading ? 'Updating...' : 'Update Questions'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SecurityQuestionsSection;
